import React, { useState } from 'react';
import { BellIcon, MessageCircleIcon, EyeIcon, EyeOffIcon, TrendingUpIcon, ArrowRightLeftIcon, ReceiptIcon, PlusIcon, LayoutGridIcon, ArrowUpRightIcon, ArrowDownLeftIcon, SparklesIcon, ChevronRightIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { BottomNav, NavTarget } from './BottomNav';
const FAVOURITES = [{
  id: 'f1',
  name: 'Nimal',
  initials: 'NS',
  color: '#009AC7'
}, {
  id: 'f2',
  name: 'Anoma',
  initials: 'AP',
  color: '#8B5CF6'
}, {
  id: 'f3',
  name: 'Sunil',
  initials: 'SP',
  color: '#10B981'
}, {
  id: 'f4',
  name: 'Ravi',
  initials: 'RK',
  color: '#F59E0B'
}];
const RECENT = [{
  id: 'r1',
  name: 'Nimal Silva',
  note: 'Transfer · Commercial Bank',
  amount: '-12,500.00',
  direction: 'out' as const,
  when: 'Today'
}, {
  id: 'r2',
  name: 'Salary — ABC Ltd.',
  note: 'Inward credit',
  amount: '+145,000.00',
  direction: 'in' as const,
  when: 'Yesterday'
}, {
  id: 'r3',
  name: 'CEB Bill',
  note: 'Utility payment',
  amount: '-4,820.00',
  direction: 'out' as const,
  when: '24 May'
}];
export function HomeScreen({
  userName = 'Kasun',
  onBillsClick,
  onTransferClick,
  onNavigate
}: {
  userName?: string;
  onBillsClick?: () => void;
  onTransferClick?: () => void;
  onNavigate?: (t: NavTarget) => void;
}) {
  const [showBalance, setShowBalance] = useState(true);
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-384">
      <StatusBar data-id="element-385" />

      {/* Header — F-pattern anchor row */}
      <header className="flex items-center justify-between px-5 pt-2 pb-4" data-id="element-386">
        <button type="button" className="flex items-center gap-3 active:opacity-80" onClick={() => onNavigate?.('settings')} data-id="element-387">
          <span className="flex items-center justify-center rounded-full overflow-hidden shrink-0" style={{
          width: 44,
          height: 44,
          background: 'linear-gradient(135deg, #009AC7 0%, #007FA5 100%)',
          color: '#FFFFFF',
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: '0.02em'
        }} data-id="element-388">
            {userName.charAt(0)}P
          </span>
          <div className="text-left" data-id="element-389">
            <div style={{
            fontSize: 11.5,
            color: '#64748B',
            fontWeight: 500,
            letterSpacing: '0.01em'
          }} data-id="element-390">
              Welcome back 👋
            </div>
            <div className="tracking-tight" style={{
            fontSize: 15,
            fontWeight: 700,
            color: '#1A1A1A',
            lineHeight: 1.2
          }} data-id="element-391">
              {userName} Perera
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2" data-id="element-392">
          <IconButton aria-label="Messages" data-id="element-393">
            <MessageCircleIcon size={20} style={{
            color: '#1A1A1A'
          }} data-id="element-394" />
          </IconButton>
          <IconButton aria-label="Notifications" badge data-id="element-395">
            <BellIcon size={20} style={{
            color: '#1A1A1A'
          }} data-id="element-396" />
          </IconButton>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pb-3" data-id="element-397">
        {/* Hero balance card — premium gradient with abstract mesh */}
        <div className="relative overflow-hidden rounded-[28px] p-5 mb-5" style={{
        background: 'linear-gradient(135deg, #009AC7 0%, #0086AE 50%, #005F7C 100%)',
        color: '#FFFFFF',
        boxShadow: '0 24px 48px -20px rgba(0,154,199,0.55), inset 0 1px 0 rgba(255,255,255,0.18)'
      }} data-id="element-398">
          {/* Decorative meshes (Gestalt: figure/ground) */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-12 rounded-full blur-3xl" style={{
          width: 220,
          height: 220,
          background: 'radial-gradient(circle, rgba(255,255,255,0.32) 0%, transparent 70%)'
        }} data-id="element-399" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -right-10 rounded-full blur-3xl" style={{
          width: 240,
          height: 240,
          background: 'radial-gradient(circle, rgba(0,0,0,0.25) 0%, transparent 70%)'
        }} data-id="element-400" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 80%, #fff 0, transparent 35%), radial-gradient(circle at 80% 20%, #fff 0, transparent 35%)'
        }} data-id="element-401" />

          <div className="relative flex items-center justify-between" data-id="element-402">
            <div data-id="element-403">
              <div style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              opacity: 0.85
            }} data-id="element-404">
                TOTAL BALANCE
              </div>
              <div className="mt-2 flex items-center gap-2" data-id="element-405">
                <span className="tracking-tight" style={{
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1.05
              }} data-id="element-406">
                  {showBalance ? 'LKR 482,650.00' : '•• ••••••'}
                </span>
                <button type="button" onClick={() => setShowBalance(s => !s)} aria-label={showBalance ? 'Hide balance' : 'Show balance'} className="flex items-center justify-center rounded-full transition-colors active:bg-white/10" style={{
                width: 32,
                height: 32,
                backgroundColor: 'rgba(255,255,255,0.18)'
              }} data-id="element-407">
                  {showBalance ? <EyeOffIcon size={16} data-id="element-408" /> : <EyeIcon size={16} data-id="element-409" />}
                </button>
              </div>
              <div className="mt-1" style={{
              fontSize: 12,
              opacity: 0.85,
              fontWeight: 500
            }} data-id="element-410">
                Savings · 0123 •••• 8910
              </div>
            </div>
          </div>

          <div className="relative mt-4 flex items-center gap-2" data-id="element-411">
            <span className="inline-flex items-center gap-1 rounded-full" style={{
            backgroundColor: 'rgba(255,255,255,0.18)',
            padding: '4px 9px',
            fontSize: 11,
            fontWeight: 700,
            border: '1px solid rgba(255,255,255,0.28)'
          }} data-id="element-412">
              <TrendingUpIcon size={12} strokeWidth={2.5} data-id="element-413" />
              +LKR 24,300
            </span>
            <span style={{
            fontSize: 11.5,
            opacity: 0.92,
            fontWeight: 500
          }} data-id="element-414">
              saved this month
            </span>
          </div>
        </div>

        {/* Quick Send — favourite recipients (Gestalt: similarity) */}
        <SectionHeading title="Quick send" action="See all" onAction={onTransferClick} data-id="element-415" />
        <div className="flex gap-3 overflow-x-auto -mx-5 px-5 pb-1 mb-5" style={{
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-416">
          <button type="button" onClick={onTransferClick} className="shrink-0 flex flex-col items-center gap-1.5 transition-transform active:scale-[0.95]" style={{
          width: 64
        }} data-id="element-417">
            <span className="flex items-center justify-center rounded-full" style={{
            width: 56,
            height: 56,
            backgroundColor: '#FFFFFF',
            border: '1.5px dashed #009AC7',
            color: '#009AC7'
          }} data-id="element-418">
              <PlusIcon size={22} strokeWidth={2.5} data-id="element-419" />
            </span>
            <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#475569',
            lineHeight: 1.2
          }} data-id="element-420">
              New
            </span>
          </button>
          {FAVOURITES.map(f => <button key={f.id} type="button" onClick={onTransferClick} className="shrink-0 flex flex-col items-center gap-1.5 transition-transform active:scale-[0.95]" style={{
          width: 64
        }} data-id="element-421">
              <span className="flex items-center justify-center rounded-full" style={{
            width: 56,
            height: 56,
            backgroundColor: `${f.color}18`,
            color: f.color,
            border: `1.5px solid ${f.color}30`,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '0.02em'
          }} data-id="element-422">
                {f.initials}
              </span>
              <span className="truncate w-full text-center" style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#1A1A1A',
            lineHeight: 1.2
          }} data-id="element-423">
                {f.name}
              </span>
            </button>)}
        </div>

        {/* Primary actions — 4 large tiles with colored icon wells */}
        <SectionHeading title="Quick actions" data-id="element-424" />
        <div className="grid grid-cols-4 gap-2.5 mb-5" data-id="element-425">
          <BigTile icon={<ArrowRightLeftIcon size={26} data-id="element-427" />} label="Transfer" tint="#009AC7" onClick={onTransferClick} data-id="element-426" />
          <BigTile icon={<ReceiptIcon size={26} data-id="element-429" />} label="Pay Bills" tint="#8B5CF6" onClick={onBillsClick} data-id="element-428" />
          <BigTile icon={<PlusIcon size={26} data-id="element-431" />} label="Top up" tint="#10B981" data-id="element-430" />
          <BigTile icon={<LayoutGridIcon size={26} data-id="element-433" />} label="More" tint="#F59E0B" data-id="element-432" />
        </div>

        {/* Insight strip — Aesthetic-Usability bonus */}
        <button type="button" className="w-full flex items-center gap-3 rounded-2xl bg-white px-4 py-3 mb-5 text-left transition-all active:scale-[0.99]" style={{
        border: '1px solid #E5EAF0',
        boxShadow: '0 1px 2px rgba(15,23,42,0.03)'
      }} data-id="element-434">
          <span className="flex items-center justify-center rounded-xl shrink-0" style={{
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #FACC15 0%, #F59E0B 100%)',
          color: '#FFFFFF'
        }} data-id="element-435">
            <SparklesIcon size={18} data-id="element-436" />
          </span>
          <div className="flex-1 min-w-0" data-id="element-437">
            <div style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#1A1A1A'
          }} data-id="element-438">
              You've saved 12.5% more this month
            </div>
            <div className="mt-0.5" style={{
            fontSize: 11.5,
            color: '#64748B',
            fontWeight: 500
          }} data-id="element-439">
              Open Insights to see your spending breakdown
            </div>
          </div>
          <ChevronRightIcon size={18} style={{
          color: '#94A3B8'
        }} data-id="element-440" />
        </button>

        {/* Recent activity */}
        <SectionHeading title="Recent activity" action="See all" data-id="element-441" />
        <div className="rounded-2xl bg-white overflow-hidden mb-2" style={{
        border: '1px solid #E5EAF0'
      }} data-id="element-442">
          {RECENT.map((t, i) => <div key={t.id} className="flex items-center gap-3 px-4 py-3" style={{
          borderBottom: i === RECENT.length - 1 ? 'none' : '1px solid #F1F5F9'
        }} data-id="element-443">
              <span className="shrink-0 flex items-center justify-center rounded-xl" style={{
            width: 38,
            height: 38,
            backgroundColor: t.direction === 'in' ? '#ECFDF5' : '#FEF2F2',
            color: t.direction === 'in' ? '#10B981' : '#EF4444'
          }} data-id="element-444">
                {t.direction === 'in' ? <ArrowDownLeftIcon size={18} data-id="element-445" /> : <ArrowUpRightIcon size={18} data-id="element-446" />}
              </span>
              <div className="flex-1 min-w-0" data-id="element-447">
                <div className="truncate" style={{
              fontSize: 13.5,
              fontWeight: 700,
              color: '#1A1A1A'
            }} data-id="element-448">
                  {t.name}
                </div>
                <div className="mt-0.5 truncate" style={{
              fontSize: 11.5,
              color: '#64748B',
              fontWeight: 500
            }} data-id="element-449">
                  {t.note}
                </div>
              </div>
              <div className="text-right shrink-0" data-id="element-450">
                <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: t.direction === 'in' ? '#10B981' : '#1A1A1A'
            }} data-id="element-451">
                  {t.amount}
                </div>
                <div className="mt-0.5" style={{
              fontSize: 10.5,
              color: '#94A3B8',
              fontWeight: 500
            }} data-id="element-452">
                  {t.when}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <BottomNav active="home" onNavigate={onNavigate} data-id="element-453" />
    </div>;
}
function SectionHeading({
  title,
  action,
  onAction
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) {
  return <div className="flex items-center justify-between mb-2.5 px-0.5" data-id="element-454">
      <span style={{
      fontSize: 13.5,
      fontWeight: 700,
      color: '#1A1A1A',
      letterSpacing: '0.01em'
    }} data-id="element-455">
        {title}
      </span>
      {action && <button type="button" onClick={onAction} style={{
      fontSize: 11.5,
      fontWeight: 700,
      color: '#009AC7'
    }} data-id="element-456">
          {action}
        </button>}
    </div>;
}
function BigTile({
  icon,
  label,
  tint,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  tint: string;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick} className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-white transition-all active:scale-[0.95]" style={{
    minHeight: 96,
    border: '1px solid #EEF1F5',
    boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
    padding: '12px 6px'
  }} data-id="element-457">
      <span className="flex items-center justify-center rounded-2xl" style={{
      width: 52,
      height: 52,
      backgroundColor: `${tint}18`,
      color: tint
    }} data-id="element-458">
        {icon}
      </span>
      <span className="text-center" style={{
      fontSize: 11.5,
      fontWeight: 700,
      color: '#1A1A1A',
      lineHeight: 1.2
    }} data-id="element-459">
        {label}
      </span>
    </button>;
}
function IconButton({
  children,
  badge,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  badge?: boolean;
}) {
  return <button type="button" {...rest} className="relative flex items-center justify-center rounded-full bg-white shrink-0 transition-colors active:bg-neutral-100" style={{
    width: 44,
    height: 44,
    border: '1px solid #E5EAF0'
  }} data-id="element-460">
      {children}
      {badge && <span className="absolute top-2 right-2.5 rounded-full" style={{
      width: 8,
      height: 8,
      backgroundColor: '#EF4444',
      border: '2px solid #FFFFFF'
    }} data-id="element-461" />}
    </button>;
}